import get from 'lodash/get';
import reduce from 'lodash/reduce';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import { VARIANTS } from '@jobcase/react_pattern_library/components/Notification';
import entity from './entity';
import config from '../../../../config';
import fetch, { toQueryString } from '../../../../utils/fetch';
import { VALID_EVENT_TYPES } from '../../../../utils/constants/hiringEvents';
import { setNotification } from '../../notification/actions';

const TYPES = {
  ...entity.getTypes(),
  UPDATE_REGISTRANT_STATE_REQUEST: 'hiringEvents/UPDATE_REGISTRANT_STATE_REQUEST',
  UPDATE_REGISTRANT_STATE_SUCCESS: 'hiringEvents/UPDATE_REGISTRANT_STATE_SUCCESS',
  UPDATE_REGISTRANT_STATE_ERROR: 'hiringEvents/UPDATE_REGISTRANT_STATE_ERROR',
  LOCATION_SEARCH_REQUEST: 'hiringEvents/LOCATION_SEARCH_REQUEST',
  LOCATION_SEARCH_SUCCESS: 'hiringEvents/LOCATION_SEARCH_SUCCESS',
  LOCATION_SEARCH_ERROR: 'hiringEvents/LOCATION_SEARCH_ERROR',
  GET_HIRING_EVENTS_REQUEST: 'hiringEvents/GET_HIRING_EVENTS_REQUEST',
  GET_HIRING_EVENTS_SUCCESS: 'hiringEvents/GET_HIRING_EVENTS_SUCCESS',
  GET_HIRING_EVENTS_FAILURE: 'hiringEvents/GET_HIRING_EVENTS_FAILURE',
  CREATE_HIRING_EVENT_REQUEST: 'hiringEvents/CREATE_HIRING_EVENT_REQUEST',
  CREATE_HIRING_EVENT_SUCCESS: 'hiringEvents/CREATE_HIRING_EVENT_SUCCESS',
  CREATE_HIRING_EVENT_FAILURE: 'hiringEvents/CREATE_HIRING_EVENT_FAILURE',
  CLEAR_NEW_HIRING_EVENT_ID: 'hiringEvents/CLEAR_NEW_HIRING_EVENT_ID',
  RESET_NEW_HIRING_EVENT_ERROR: 'hiringEvents/RESET_NEW_HIRING_EVENT_ERROR',
  CLEAR_STATE: 'hiringEvents/CLEAR_STATE',
  INCREMENT_TOTAL_EVENTS: 'hiringEvents/INCREMENT_TOTAL_EVENTS',
};

const register = (eventId, register, profileKey, registrantDetails) => async (dispatch) => {
  dispatch({ type: TYPES.UPDATE_REGISTRANT_STATE_REQUEST });
  // If we are not registered, we will add 1 to the current count for whichever the user is asking for, registration or interest
  // If we are interested => registered, then we will subtract 1 from interested and add 1 to registered
  // If we are registered => interested, then we will subtract 1 from registered and add 1 to interested
  const updateStatus = {
    ...register && registrantDetails.status === 'NONE' && { registrant_count: registrantDetails.registrant_count + 1 },
    ...register && registrantDetails.status === 'INTERESTED'
    && {
      registrant_count: registrantDetails.registrant_count + 1,
      interested_count: registrantDetails.interested_count - 1,
    },
    ...!register && registrantDetails.status === 'NONE' && { interested_count: registrantDetails.interested_count + 1 },
    ...!register && registrantDetails.status === 'CONFIRMED'
    && {
      registrant_count: registrantDetails.registrant_count - 1,
      interested_count: registrantDetails.interested_count + 1,
    },
  };

  try {
    const response = await entity.api.register({ eventId, register, profileKey });
    const status = get(response, 'data.status', '');
    const firstName = get(response, 'data.first_name', '');
    const lastName = get(response, 'data.last_name', '');
    const emailAddress = get(response, 'data.email_address', '');
    const phoneNumber = get(response, 'data.phone_number', '');

    dispatch({ type: TYPES.UPDATE_REGISTRANT_STATE_SUCCESS,
      payload: {
        [eventId]: {
          registrant_details: {
            status,
            first_name: firstName,
            last_name: lastName,
            email_address: emailAddress,
            phone_number: phoneNumber,
            ...updateStatus,
          },
        },
      },
    });

    return response;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_REGISTRANT_STATE_ERROR, payload: error });
    throw error;
  }
};

const unregister = (eventId, profileKey, registrantDetails) => async (dispatch) => {
  dispatch({ type: TYPES.UPDATE_REGISTRANT_STATE_REQUEST });
  // If we are registered, remove us from registered
  // If we are interested, remove us from interested
  const updateStatus = {
    ...registrantDetails.status === 'INTERESTED' && { interested_count: registrantDetails.interested_count - 1 },
    ...registrantDetails.status === 'CONFIRMED' && { registrant_count: registrantDetails.registrant_count - 1 },
  };

  try {
    await entity.api.unregister(eventId, profileKey);
    dispatch({ type: TYPES.UPDATE_REGISTRANT_STATE_SUCCESS,
      payload: {
        [eventId]: {
          registrant_details: {
            status: 'NONE',
            first_name: null,
            last_name: null,
            email_address: null,
            phone_number: null,
            ...updateStatus,
          },
        },
      },
    });

    return true;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_REGISTRANT_STATE_ERROR, payload: error });
    throw error;
  }
};

const getEvent = options => async (dispatch) => {
  dispatch({ type: TYPES.GET_HIRING_EVENTS_REQUEST, payload: { page: 1 } });
  try {
    const response = await entity.api.getEvent(options);
    const data = get(response, 'data');
    const eventId = get(options, 'eventId');
    dispatch({ type: TYPES.GET_REQUEST_SUCCESS, payload: { [eventId]: data } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading the hiring event. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading the hiring event.', VARIANTS.INFO));
    }
    throw error;
  }
};

const createHiringEvent = data => async (dispatch) => {
  dispatch({ type: TYPES.CREATE_HIRING_EVENT_REQUEST });
  try {
    const response = await entity.api.createEvent(data);
    const eventData = get(response, 'data');
    dispatch({ type: TYPES.CREATE_HIRING_EVENT_SUCCESS, payload: { [eventData.id]: eventData, id: eventData.id } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.CREATE_HIRING_EVENT_FAILURE, payload: error });

    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while creating the hiring event. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error?.message.includes('Zip code not found')) {
      dispatch(setNotification('An error occurred while creating the hiring event.', VARIANTS.INFO));
    }
    throw error;
  }
};

const updateHiringEvent = (data, noLoader = false) => async (dispatch) => {
  if (!noLoader) {
    dispatch({ type: TYPES.UPDATE_REQUEST });
  }
  try {
    const response = await entity.api.updateHiringEvent(data);
    const event = get(response, 'data');
    dispatch({ type: TYPES.UPDATE_REQUEST_SUCCESS, payload: { [event.id]: event } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.UPDATE_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while updating the hiring event. ${error.errors[0]}`, VARIANTS.INFO));
    } else if (!error?.message.includes('Zip code not found')) {
      dispatch(setNotification('An error occurred while updating the hiring event.', VARIANTS.INFO));
    }
    throw error;
  }
};

const getAllHiringEvents = (options, profileKey) => async (dispatch, getState) => {
  dispatch({ type: TYPES.GET_HIRING_EVENTS_REQUEST, payload: { page: options.page_index, initialLoading: true } });
  try {
    const response = await entity.api.getEvents(options, profileKey);
    const data = get(response, 'data');
    const content = get(data, 'content');
    const filteredContent = filter(content, event => VALID_EVENT_TYPES.includes(get(event, 'event_type')));
    const hiringList = reduce(filteredContent, (result, entity) => ({ ...result, [entity.id]: entity }), {});
    data.content = hiringList;

    const state = getState();
    const initialLoading = state?.entities?.hiringEvents?.initialLoading;
    if (!(initialLoading && data?.number !== 0)) {
      dispatch({ type: TYPES.GET_HIRING_EVENTS_SUCCESS, payload: data });
    }
    return response;
  } catch (error) {
    dispatch({ type: TYPES.GET_HIRING_EVENTS_FAILURE, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while loading hiring events. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while loading hiring events.', VARIANTS.INFO));
    }

    throw error;
  }
};

const parseEventsWithDataResponse = (response) => {
  const { content, ...pagination } = response.data;

  const normalizedEventData = reduce(content, (result, event) => ({
    eventIds: [...result.eventIds, event.id],
    eventMap: { ...result.eventMap, [event.id]: { ...event } },
  }), { eventIds: [], eventMap: {} });

  return {
    eventIds: normalizedEventData.eventIds,
    eventMap: normalizedEventData.eventMap,
    pagination,
  };
};

const locationSearch = options => async (dispatch) => {
  dispatch({ type: TYPES.LOCATION_SEARCH_REQUEST });
  const query = get(options, 'query', {});
  const fetchOptions = get(options, 'fetch', {});
  try {
    const response = await fetch(`${config.apiUrl}/v2/hiring-event/location_search${toQueryString(query)}`, {
      ...fetchOptions,
      method: 'GET',
    });
    const { eventIds, eventMap, pagination } = parseEventsWithDataResponse(response);
    dispatch({ type: TYPES.SEARCH_REQUEST_SUCCESS, payload: eventMap });
    return {
      eventIds,
      eventMap,
      pagination,
    };
  } catch (error) {
    dispatch({ type: TYPES.SEARCH_REQUEST_ERROR, payload: error });
    throw error;
  }
};

const deleteHiringEvent = eventId => async (dispatch) => {
  try {
    const response = await entity.api.deleteHiringEvent(eventId);
    await dispatch({ type: TYPES.DESTROY_REQUEST_SUCCESS, payload: { [eventId]: null } });
    return response;
  } catch (error) {
    dispatch({ type: TYPES.DESTROY_REQUEST_ERROR, payload: error });
    if (!isEmpty(error.errors)) {
      dispatch(setNotification(`An error occurred while deleting the hiring event. ${error.errors[0]}`, VARIANTS.INFO));
    } else {
      dispatch(setNotification('An error occurred while deleting the hiring event.', VARIANTS.INFO));
    }

    throw error;
  }
};

const clearNewEventId = () => ({ type: TYPES.CLEAR_NEW_HIRING_EVENT_ID });

const clearState = () => (dispatch) => {
  dispatch({ type: TYPES.CLEAR_STATE });
};

const resetNewEventError = () => ({ type: TYPES.RESET_NEW_HIRING_EVENT_ERROR });

const incrementTotalEventsCount = () => ({ type: TYPES.INCREMENT_TOTAL_EVENTS });

export {
  TYPES,
};

export default {
  TYPES,
  ...entity.getActions(),
  register,
  unregister,
  clearState,
  getEvent,
  getAllHiringEvents,
  locationSearch,
  createHiringEvent,
  updateHiringEvent,
  clearNewEventId,
  resetNewEventError,
  deleteHiringEvent,
  incrementTotalEventsCount,
};
